import { BrowserRouter as Router, Routes, Route, Link, useLocation, Navigate, useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { HomeModeContext } from './contexts/HomeModeContext';
import { Home } from './components/pages/Home';
import { Login } from './components/pages/Login';
import { UploadChoice } from './components/pages/UploadChoice';
import { UploadPrivate } from './components/pages/UploadPrivate';
import { PublicData } from './components/pages/PublicData';
import DatasetPreparation from './components/pages/DatasetPreparation';
import { SelectSignature } from './components/pages/SelectSignature';
import { JobQueue } from './components/pages/JobQueue';
import { Results } from './components/pages/Results';
import { Account } from './components/pages/Account';
import { Checkout } from './components/pages/Checkout';
import { CheckoutSuccess } from './components/pages/CheckoutSuccess';
import { ErrorPage } from './components/pages/ErrorPage';
import { Impressum } from './components/pages/Impressum';
import { Demo } from './components/pages/Demo';
import { SignOut, User, IconContext, ArrowLeft } from '@phosphor-icons/react';
import { CellRevealIcon } from './components/ui/CellRevealLogo';

function NotFound() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-background flex items-center justify-center pt-16">
      <div className="text-center max-w-sm px-6">
        <p className="font-mono text-[10px] tracking-[0.35em] text-muted-foreground/40 uppercase mb-4">404</p>
        <h1 className="text-2xl font-bold text-foreground tracking-tight mb-2">Page not found</h1>
        <p className="text-muted-foreground text-sm leading-relaxed mb-8">
          The page you're looking for doesn't exist or was moved.
        </p>
        <button
          onClick={() => navigate('/')}
          className="inline-flex items-center gap-2 text-sm font-medium text-teal-700 hover:text-teal-900 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to home
        </button>
      </div>
    </div>
  );
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuth();
  const location = useLocation();

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return <>{children}</>;
}

function Navigation({ isDark }: { isDark: boolean }) {
  const location = useLocation();
  const { isAuthenticated, logout, user } = useAuth();
  const [isAtTop, setIsAtTop] = useState(true);

  const isActive = (path: string) => location.pathname === path;
  const isHome = location.pathname === '/';
  // Light nav: only on the home page when the page is in dark mode
  const light = isHome && isDark;

  useEffect(() => {
    const handleScroll = () => {
      setIsAtTop(window.scrollY < 50);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Don't show navigation on login page
  if (location.pathname === '/login') {
    return null;
  }

  return (
    <div className={`fixed z-50 transition-all duration-500 ease-out ${
      isAtTop
        ? 'top-0 left-0 right-0'
        : 'top-4 left-4 right-4 sm:left-8 sm:right-8 md:left-12 md:right-12 lg:left-24 lg:right-24'
    }`}>
      <nav className={`backdrop-blur-md transition-all duration-500 border ${
        light
          ? isAtTop
            ? 'bg-white/85 border-transparent'
            : 'bg-white/95 rounded-2xl border-slate-200/80 shadow-md'
          : isAtTop
            ? 'bg-navy-900/80 border-transparent'
            : 'bg-navy-900/90 rounded-2xl border-white/20 shadow-lg'
      }`}>
        <div className={`mx-auto px-4 sm:px-6 lg:px-8 transition-all duration-500 ${
          isAtTop ? 'max-w-7xl' : 'max-w-6xl'
        }`}>
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center gap-3">
              <CellRevealIcon variant={light ? 'light' : 'dark'} size="md" pulseGlow />
              <span style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", lineHeight: 1, userSelect: 'none' }}>
                <span
                  className="text-2xl font-light tracking-tight"
                  style={{ letterSpacing: '-0.022em', color: light ? '#0f766e' : 'rgb(204 251 241)', transition: 'color 0.3s' }}
                >
                  Cell
                </span>
                <span
                  className="text-2xl font-bold tracking-tight"
                  style={{ letterSpacing: '-0.042em', color: light ? '#134e4a' : '#fef3c7', transition: 'color 0.3s' }}
                >
                  Reveal
                </span>
              </span>
            </Link>

            <div className="flex items-center gap-6">
              {isAuthenticated ? (
                <>
                  <Link
                    to="/upload"
                    className={`text-sm transition-colors ${
                      isActive('/upload') || isActive('/select-signature')
                        ? light ? 'text-teal-900 font-medium' : 'text-white font-medium'
                        : light ? 'text-teal-700/70 hover:text-teal-900' : 'text-teal-100/75 hover:text-white'
                    }`}
                  >
                    New Analysis
                  </Link>
                  <Link
                    to="/jobs"
                    className={`text-sm transition-colors ${
                      isActive('/jobs')
                        ? light ? 'text-teal-900 font-medium' : 'text-white font-medium'
                        : light ? 'text-teal-700/70 hover:text-teal-900' : 'text-teal-100/75 hover:text-white'
                    }`}
                  >
                    My Analyses
                  </Link>

                  <div className={`flex items-center gap-3 pl-6 border-l transition-colors duration-300 ${light ? 'border-teal-200/80' : 'border-teal-800/60'}`}>
                    <Link
                      to="/account"
                      className={`flex items-center gap-2 transition-colors ${
                        isActive('/account')
                          ? light ? 'text-teal-900' : 'text-white'
                          : light ? 'text-teal-700 hover:text-teal-900' : 'text-teal-100 hover:text-white'
                      }`}
                    >
                      <User className="w-5 h-5" />
                      <span>{user?.name.split(' ')[0]}</span>
                    </Link>
                    <button
                      onClick={logout}
                      className={`flex items-center gap-2 transition-colors ${light ? 'text-teal-700 hover:text-teal-900' : 'text-teal-100 hover:text-white'}`}
                      title="Sign out"
                    >
                      <SignOut className="w-5 h-5" />
                    </button>
                  </div>
                </>
              ) : (
                <a
                  href="mailto:hello@cellreveal.com"
                  className={`px-5 py-2 font-medium rounded-lg transition-colors ${
                    light
                      ? 'bg-teal-600 text-white hover:bg-teal-700'
                      : 'bg-teal-500/20 border border-teal-400/50 text-teal-100 hover:bg-teal-500/30 hover:text-white'
                  }`}
                >
                  Request access
                </a>
              )}
            </div>
          </div>
        </div>
      </nav>
    </div>
  );
}

function AppContent() {
  const [isDark, setIsDark] = useState(false);

  return (
    <HomeModeContext.Provider value={{ isDark, setIsDark }}>
      <IconContext.Provider value={{ weight: "bold" }}>
        <div className="min-h-screen bg-background">
          <a
            href="#main-content"
            className="sr-only focus:not-sr-only focus:fixed focus:top-4 focus:left-4 focus:z-[100] focus:px-4 focus:py-2 focus:bg-teal-600 focus:text-white focus:rounded-lg focus:text-sm focus:font-medium"
          >
            Skip to content
          </a>
          <Navigation isDark={isDark} />
          <main id="main-content">
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/login" element={<Login />} />
            <Route path="/impressum" element={<Impressum />} />
            <Route path="/demo" element={<Demo />} />

            <Route path="/error" element={<ErrorPage />} />

            {/* Protected Routes */}
            <Route path="/upload" element={
              <ProtectedRoute>
                <UploadChoice />
              </ProtectedRoute>
            } />
            <Route path="/upload/private" element={
              <ProtectedRoute>
                <UploadPrivate />
              </ProtectedRoute>
            } />
            <Route path="/public-data" element={
              <ProtectedRoute>
                <PublicData />
              </ProtectedRoute>
            } />
            <Route path="/dataset-preparation" element={
              <ProtectedRoute>
                <DatasetPreparation />
              </ProtectedRoute>
            } />
            <Route path="/select-signature" element={
              <ProtectedRoute>
                <SelectSignature />
              </ProtectedRoute>
            } />
            <Route path="/jobs" element={
              <ProtectedRoute>
                <JobQueue />
              </ProtectedRoute>
            } />
            <Route path="/results/:jobId" element={
              <ProtectedRoute>
                <Results />
              </ProtectedRoute>
            } />
            <Route path="/account" element={
              <ProtectedRoute>
                <Account />
              </ProtectedRoute>
            } />
            <Route path="/checkout" element={
              <ProtectedRoute>
                <Checkout />
              </ProtectedRoute>
            } />
            <Route path="/checkout/success" element={
              <ProtectedRoute>
                <CheckoutSuccess />
              </ProtectedRoute>
            } />
            <Route path="*" element={<NotFound />} />
          </Routes>
          </main>
        </div>
      </IconContext.Provider>
    </HomeModeContext.Provider>
  );
}

export default function App() {
  return (
    <Router>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </Router>
  );
}
